import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Tenant, TenantMembershipWithDetails, TenantStats } from '@/types'
import { tenants, handleApiResponse } from '@/services/api'
import toast from 'react-hot-toast'

interface TenantState {
  currentTenant: Tenant | null
  currentMembership: TenantMembershipWithDetails | null
  availableTenants: Tenant[]
  tenantStats: TenantStats | null
  isLoading: boolean
  error: string | null

  // Actions
  setCurrentTenant: (tenant: Tenant | null) => void
  setCurrentMembership: (membership: TenantMembershipWithDetails | null) => void
  fetchTenants: () => Promise<void>
  switchTenant: (tenantId: number) => Promise<void>
  fetchTenantStats: (tenantId?: number) => Promise<void>
  refreshCurrentTenant: () => Promise<void>
  clearTenant: () => void

  // Getters
  hasPermission: (permission: string) => boolean
  isOwner: () => boolean
  isAdmin: () => boolean
  canManageMembers: () => boolean
  canManagePrinters: () => boolean
}

const TENANT_STORAGE_KEY = 'bonnycloud-current-tenant-id'

// Helper: Get stored tenant ID from localStorage
const getStoredTenantId = (): number | null => {
  const stored = localStorage.getItem(TENANT_STORAGE_KEY)
  return stored ? parseInt(stored, 10) : null
}

// Helper: Store tenant ID in localStorage
const storeTenantId = (tenantId: number | null): void => {
  if (tenantId) {
    localStorage.setItem(TENANT_STORAGE_KEY, tenantId.toString())
  } else {
    localStorage.removeItem(TENANT_STORAGE_KEY)
  }
}

// Helper: Check if user has a specific permission
const checkPermission = (
  membership: TenantMembershipWithDetails | null,
  permission: string
): boolean => {
  if (!membership) return false

  const { tenantRole, customPermissions } = membership

  // Owner and admin have all permissions
  if (tenantRole === 'owner' || tenantRole === 'admin') {
    return true
  }

  // Check custom permissions
  if (customPermissions && permission in customPermissions) {
    return customPermissions[permission] === true
  }

  // Default member permissions based on role
  const defaultMemberPermissions = {
    'can_view_printers': true,
    'can_print': true,
    'can_view_devices': true,
    'can_view_jobs': true,
  }

  const defaultViewerPermissions = {
    'can_view_printers': true,
    'can_view_devices': true,
    'can_view_jobs': true,
  }

  if (tenantRole === 'member') {
    return defaultMemberPermissions[permission] === true
  }

  if (tenantRole === 'viewer') {
    return defaultViewerPermissions[permission] === true
  }

  return false
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      currentTenant: null,
      currentMembership: null,
      availableTenants: [],
      tenantStats: null,
      isLoading: false,
      error: null,

      setCurrentTenant: (tenant: Tenant | null) => {
        set({ currentTenant: tenant })
        storeTenantId(tenant?.id ?? null)
      },

      setCurrentMembership: (membership: TenantMembershipWithDetails | null) => {
        set({ currentMembership: membership })
      },

      fetchTenants: async () => {
        set({ isLoading: true, error: null })

        try {
          const response = await tenants.getAll()
          const tenantsData = handleApiResponse<Tenant[]>(response)

          set({ availableTenants: tenantsData, isLoading: false })

          // Auto-select tenant if none selected
          const { currentTenant } = get()
          if (!currentTenant && tenantsData.length > 0) {
            const storedId = getStoredTenantId()
            const tenantToSelect = storedId
              ? tenantsData.find(t => t.id === storedId) || tenantsData[0]
              : tenantsData[0]

            await get().switchTenant(tenantToSelect.id)
          }
        } catch (error: any) {
          const errorMessage = error.message || 'Failed to load tenants'
          set({ error: errorMessage, isLoading: false })
          toast.error(errorMessage)
        }
      },

      switchTenant: async (tenantId: number) => {
        set({ isLoading: true, error: null })

        try {
          // Call backend to set active tenant
          await tenants.switchActive(tenantId)

          // Fetch tenant details
          const tenantResponse = await tenants.get(tenantId)
          const tenant = handleApiResponse<Tenant>(tenantResponse)

          // Fetch membership details for permissions
          const membershipsResponse = await tenants.getMemberships()
          const memberships = handleApiResponse<TenantMembershipWithDetails[]>(membershipsResponse)
          const membership = memberships.find(m => m.tenantId === tenantId)

          set({
            currentTenant: tenant,
            currentMembership: membership || null,
            isLoading: false
          })

          storeTenantId(tenantId)

          // Fetch stats for the new tenant
          await get().fetchTenantStats(tenantId)

        } catch (error: any) {
          const errorMessage = error.message || 'Failed to switch tenant'
          set({ error: errorMessage, isLoading: false })
          toast.error(errorMessage)
        }
      },

      fetchTenantStats: async (tenantId?: number) => {
        const targetTenantId = tenantId || get().currentTenant?.id
        if (!targetTenantId) return

        try {
          const response = await tenants.getStats(targetTenantId)
          const stats = handleApiResponse<TenantStats>(response)
          set({ tenantStats: stats })
        } catch (error: any) {
          console.error('Failed to fetch tenant stats:', error)
          // Don't show error toast for stats - it's not critical
        }
      },

      refreshCurrentTenant: async () => {
        const { currentTenant } = get()
        if (currentTenant) {
          await get().switchTenant(currentTenant.id)
        }
      },

      clearTenant: () => {
        set({
          currentTenant: null,
          currentMembership: null,
          tenantStats: null,
          error: null
        })
        storeTenantId(null)
      },

      // Permission checkers
      hasPermission: (permission: string) => {
        const { currentMembership } = get()
        return checkPermission(currentMembership, permission)
      },

      isOwner: () => {
        const { currentMembership } = get()
        return currentMembership?.tenantRole === 'owner'
      },

      isAdmin: () => {
        const { currentMembership } = get()
        return currentMembership?.tenantRole === 'admin' || currentMembership?.tenantRole === 'owner'
      },

      canManageMembers: () => {
        return get().isAdmin() || get().hasPermission('can_manage_members')
      },

      canManagePrinters: () => {
        return get().isAdmin() || get().hasPermission('can_manage_printers')
      },
    }),
    {
      name: 'bonnycloud-tenant',
      partialize: (state) => ({
        currentTenant: state.currentTenant,
        currentMembership: state.currentMembership,
        availableTenants: state.availableTenants,
      }),
    }
  )
)
